import React from 'react';
import SectionLabel from './SectionLabel';

const problems = [
  {
    num: '01',
    title: 'Active marketing. Weak growth.',
    sub: 'Content is going out. Ads are running. But revenue is inconsistent and nothing compounds.',
  },
  {
    num: '02',
    title: 'Scattered brand identity.',
    sub: "Different visuals, tones, and messages across platforms — the brand doesn't hold a position.",
  },
  {
    num: '03',
    title: 'No system connecting visibility to conversion.',
    sub: "People see the brand but don't take action. The path from attention to revenue is broken.",
  },
  {
    num: '04',
    title: 'Disorganised backend operations.',
    sub: 'Follow-up is manual, client onboarding is inconsistent, and internal workflow slows everything down.',
  },
  {
    num: '05',
    title: 'Paid ads without infrastructure.',
    sub: "Ad spend is burning without a proper funnel, retargeting strategy, or conversion system behind it.",
  },
];

const Problem: React.FC = () => {
  return (
    <section
      id="problem"
      className="relative py-[120px] px-10"
      style={{
        background: 'linear-gradient(158deg, #faf4e8 0%, #f5ecce 38%, #ede0b4 72%, #e8d89e 100%)',
        borderTop: '1px solid rgba(184,154,90,0.18)',
        borderBottom: '1px solid rgba(184,154,90,0.18)',
      }}
    >
      {/* Subtle ambient glow — top-right */}
      <div
        className="absolute top-0 right-0 pointer-events-none"
        style={{
          width: 460,
          height: 460,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(184,154,90,0.12) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
      />

      <div className="max-w-[1200px] mx-auto relative z-10">
        <SectionLabel>The Problem</SectionLabel>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-20 mt-16">

          {/* Left — narrative copy */}
          <div>
            {[
              <>You've invested in content. You've run campaigns. You've hired someone to post. On paper, the marketing is active.</>,
              <>But growth is inconsistent. The brand feels scattered. The backend is messy. And nothing quite connects —{' '}
                <strong style={{ color: '#1a130a', fontWeight: 400 }}>
                  visibility doesn't translate to conversion, and effort doesn't compound.
                </strong>
              </>,
              <>This is not a content problem. It is a structure problem. And structure is what we build.</>,
            ].map((text, i) => (
              <p
                key={i}
                style={{
                  fontSize: 14,
                  lineHeight: 2.2,
                  color: '#5a4e38',
                  marginBottom: 20,
                  letterSpacing: '0.02em',
                }}
              >
                {text}
              </p>
            ))}
          </div>

          {/* Right — problem list */}
          <ul className="list-none">
            {problems.map((p, i) => (
              <li
                key={p.num}
                className="fade-up flex items-start gap-5 py-6"
                style={{
                  borderBottom: '1px solid rgba(184,154,90,0.2)',
                  borderTop: i === 0 ? '1px solid rgba(184,154,90,0.2)' : undefined,
                }}
              >
                <span
                  style={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: 11,
                    fontWeight: 500,
                    color: '#b89a5a',
                    letterSpacing: '0.12em',
                    minWidth: 28,
                    paddingTop: 2,
                  }}
                >
                  {p.num}
                </span>
                <div>
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 400,
                      color: '#1a130a',
                      lineHeight: 1.7,
                      letterSpacing: '0.02em',
                    }}
                  >
                    {p.title}
                    <span
                      className="block"
                      style={{
                        fontSize: 12,
                        fontWeight: 300,
                        color: '#7a6e56',
                        marginTop: 4,
                      }}
                    >
                      {p.sub}
                    </span>
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Problem;